import React, { Fragment, ReactNode, useState } from 'react';

import { Color, ComponentSize, IDesignTokens } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { COMPETITIONLOGO, placeholdersMap } from '../../../utils/placeholders-football';
import { placeholdersMap as placeholdersTennis, TOURNAMENTFLAG } from '../../../utils/placeholders-tennis';
import { makeClassNames } from '../../../utils/styles';
import Cta from '../../atoms/cta/cta';
import IconAccordionUp from '../../atoms/icons/icon-accordion-up';
import ImgPlaceholder from '../../atoms/img-placeholder/img-placeholder';
import Text from '../../atoms/text/text';

import './accordion.scss';

type variants = 'default' | 'gray-background';

export interface IAccordionTitle extends IDesignTokens {
  text?: string;
  url?: string | null;
}
export interface IAccordion extends IComponent {
  arrow?: ReactNode;
  cta?: IAccordionTitle;
  id: number | string;
  isOpen?: boolean;
  isUpperCase?: boolean;
  logoUrl?: string;
  variant?: variants;
  title?: IAccordionTitle;
}

const ACCORDION_ICON_SIZE: ComponentSize = 'xxsmall';
const ACCORDION_ICON_COLOR: Color = 'inherit';

export const defaultArrow = () => <IconAccordionUp size={ACCORDION_ICON_SIZE} color={ACCORDION_ICON_COLOR} />;

const classNames = ({ isUpperCase, variant }: { isUpperCase: boolean; variant: IAccordion['variant'] }) => {
  return makeClassNames({
    cta: 'accordion__cta',
    content: ['accordion__content'],
    header: ['accordion__header'],
    headerArrow: ['accordion__header-arrow'],
    headerBar: 'accordion__header-bar',
    inputbox: 'accordion__input-box',
    logo: 'accordion__logo',
    title: ['accordion__title', { 'accordion__title--upper-case': isUpperCase }],
    wrapper: ['accordion', `accordion--${variant}`], // TAG is used to CUSTOMIZE CSS, e.g. for a FOOTBALL vs TENNIS accordion
  });
};

const Accordion = ({
  /* istanbul ignore next: default value */ arrow = defaultArrow(),
  /* istanbul ignore next: default value */ children,
  /* istanbul ignore next: default value */ isOpen = false,
  /* istanbul ignore next: default value */ isUpperCase = false,
  /* istanbul ignore next: default value */ variant = 'default',
  /* istanbul ignore next: default value */ trackingData = {},
  /* istanbul ignore next: default value */ title = {
    text: '',
  },
  cta,
  id,
  logoUrl,
}: IAccordion) => {
  const [expanded, setExpanded] = useState<boolean>(isOpen);
  const [focus, setFocus] = useState<boolean>(false);
  const [tabActive, setTabActive] = useState<boolean>(true);

  const cn = classNames({ isUpperCase, variant });
  const {
    text: titleText,
    color: titleColor,
    fontSize: titleFontSize,
    fontWeight: titleFontWeight,
    url: titleUrl,
  } = title;

  const titleComponent = (
    <Fragment>
      {logoUrl && (
        <ImgPlaceholder
          role="presentation"
          className={cn.logo}
          src={logoUrl}
          placeholder={
            variant === 'gray-background' ? placeholdersTennis[TOURNAMENTFLAG] : placeholdersMap[COMPETITIONLOGO]
          }
        />
      )}
      <Text color={titleColor} fontSize={titleFontSize} fontWeight={titleFontWeight} className={cn.title}>
        {titleText}
      </Text>
    </Fragment>
  );

  const linkableTitle = titleUrl ? (
    <Cta href={titleUrl} uppercase={false} narrow trackingData={trackingData} hasWrapper={false}>
      {titleComponent}
    </Cta>
  ) : (
    titleComponent
  );

  const linkableCta: JSX.Element | null =
    cta && cta.url ? (
      <Text className={cn.cta} fontSize={cta.fontSize} fontWeight={cta.fontWeight}>
        <Cta href={cta.url} hasWrapper={false}>
          {cta.text}
        </Cta>
      </Text>
    ) : null;

  return (
    <Fragment>
      <div className={cn.wrapper} data-focused={tabActive && focus}>
        <input
          className={cn.inputbox}
          type="checkbox"
          id={`accordion-chck${id}`}
          defaultChecked={isOpen}
          aria-controls="content"
          onClick={() => {
            setTabActive(false);
            expanded ? setExpanded(false) : setExpanded(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpanded(!expanded);
            }
          }}
          onFocus={() => setFocus(true)}
          onBlur={() => {
            setFocus(false);
            setTabActive(true);
          }}
          aria-expanded={expanded}
          checked={expanded}
        />
        <label htmlFor={`accordion-chck${id}`}>
          <div className={cn.header}>
            {linkableTitle}
            <div className={cn.headerBar}>
              {linkableCta}
              <div className={cn.headerArrow}>{arrow}</div>
            </div>
          </div>
        </label>
        <div className={cn.content} id="content" role="region">
          {children}
        </div>
      </div>
    </Fragment>
  );
};

export default Accordion;
